import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { RESIDENCY_LABELS, RESIDENCY_FULL } from '../lib/residency';
import { Building2, MapPin, Globe2, Phone, ArrowRight, Mail, User, CheckCircle2, ShieldCheck, Calendar, TrendingUp, Lock, CalendarX } from 'lucide-react';

// Iter 98 — short labels appear on the badge; long labels go in the tooltip
// so the trust-signal carries both the at-a-glance flag and the data centre.
// Iter 122 — labels sourced from /app/frontend/src/lib/residency.js so all
// residency-aware surfaces stay in sync.

/**
 * Public profile page for a single tenant clinic.
 * Lives at /c/<slug>. Shows logo, tagline, address, phone, website, plus a list
 * of public clinicians. Patients can register or sign in to book.
 */
export default function PublicClinic() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [trust, setTrust] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/public/clinics/${slug}`)
      .then(r => setClinic(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Clinic not found'));
    // Trust badge is non-critical — silently swallow errors.
    api.get(`/public/clinics/${slug}/trust`)
      .then(r => setTrust(r.data))
      .catch(() => setTrust(null));
  }, [slug]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF6EE]">
      <div className="text-center">
        <Building2 className="w-12 h-12 mx-auto text-fg-muted" />
        <p className="mt-4 text-fg-muted">{err}</p>
        <Link to="/clinics" className="btn-primary text-sm mt-6 inline-flex items-center gap-2">← Back to directory</Link>
      </div>
    </div>
  );
  if (!clinic) return <div className="min-h-screen flex items-center justify-center bg-[#FAF6EE] text-fg-muted">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAF6EE]">
      {/* Hero */}
      <header className="bg-[#003D80] text-white py-12 md:py-20 px-6 md:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4">
            {clinic.custom_logo_url
              ? <img src={clinic.custom_logo_url} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-[#FFB000]/30" />
              : <div className="w-20 h-20 rounded-xl bg-[#FFB000]/20 flex items-center justify-center"><Building2 className="w-8 h-8 text-[#FFB000]" /></div>}
            <div>
              <Link to="/clinics" className="text-xs uppercase tracking-[0.2em] text-[#FFB000] font-medium hover:underline" data-testid="back-to-directory">← BACK TO DIRECTORY</Link>
              <h1 className="text-white text-3xl md:text-5xl font-heading font-bold mt-2 leading-tight">{clinic.name}</h1>
              {clinic.country_code && <div className="text-sm text-white/80 mt-1">{clinic.country_code}</div>}
            </div>
          </div>
          {clinic.custom_tagline && (
            <p className="text-lg md:text-xl text-white/80 italic mt-6 max-w-3xl">"{clinic.custom_tagline}"</p>
          )}

          {/* Trust signals — verified email + sessions delivered + tenure.
              Only render when the trust call succeeded; degrades gracefully. */}
          {trust && (trust.verified || trust.member_since || trust.sessions_completed > 0) && (
            <div className="mt-6 inline-flex flex-wrap items-center gap-2" data-testid="trust-badges">
              {trust.verified && (
                <span className="inline-flex items-center gap-1.5 bg-[#1B5E20]/20 border border-[#5DBB66]/40 text-[#9CE6A2] text-xs px-2.5 py-1 rounded-full backdrop-blur-sm" data-testid="trust-verified">
                  <ShieldCheck className="w-3.5 h-3.5"/>
                  <span className="font-semibold">Verified clinic</span>
                </span>
              )}
              {trust.sessions_completed > 0 && (
                <span className="inline-flex items-center gap-1.5 bg-[#FFB000]/15 border border-[#FFB000]/40 text-[#FFB000] text-xs px-2.5 py-1 rounded-full backdrop-blur-sm" data-testid="trust-sessions">
                  <TrendingUp className="w-3.5 h-3.5"/>
                  <span className="font-semibold">
                    {trust.sessions_completed < trust.sessions_exact_under
                      ? `${trust.sessions_completed} session${trust.sessions_completed === 1 ? '' : 's'} delivered`
                      : `${trust.sessions_completed}+ sessions delivered`}
                  </span>
                </span>
              )}
              {trust.member_since && (
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white/90 text-xs px-2.5 py-1 rounded-full backdrop-blur-sm" data-testid="trust-tenure">
                  <Calendar className="w-3.5 h-3.5"/>
                  <span className="font-semibold">Member since {trust.member_since}</span>
                </span>
              )}
              {trust.privacy_first?.earned && (
                <span className="inline-flex items-center gap-1.5 bg-[#003D80]/40 border border-[#5DBB66]/60 text-[#9CE6A2] text-xs px-2.5 py-1 rounded-full backdrop-blur-sm"
                      data-testid="trust-privacy-first"
                      title={`GDPR self-service score: ${trust.privacy_first.score}/100`}>
                  <Lock className="w-3.5 h-3.5"/>
                  <span className="font-semibold">Privacy-First</span>
                </span>
              )}
              {trust.data_residency && (
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white/90 text-xs px-2.5 py-1 rounded-full backdrop-blur-sm"
                      data-testid="trust-residency"
                      title={`Patient data is stored in: ${RESIDENCY_FULL[trust.data_residency] || trust.data_residency.toUpperCase()}`}>
                  <Globe2 className="w-3.5 h-3.5"/>
                  <span className="font-semibold">{RESIDENCY_LABELS[trust.data_residency] || trust.data_residency.toUpperCase()}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 grid md:grid-cols-3 gap-6">
        {/* Contact card */}
        <aside className="surface border border-soft rounded-xl p-5 h-fit">
          <h2 className="text-xs uppercase tracking-wider text-fg-muted mb-3">Contact</h2>
          <ul className="space-y-3 text-sm">
            {clinic.address && <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-brand shrink-0 mt-0.5" /><span>{clinic.address}</span></li>}
            {clinic.phone && <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand shrink-0" /><a href={`tel:${clinic.phone}`} className="hover:underline">{clinic.phone}</a></li>}
            {clinic.website && <li className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-brand shrink-0" /><a href={clinic.website} target="_blank" rel="noreferrer" className="hover:underline truncate">{clinic.website.replace(/^https?:\/\//, '')}</a></li>}
          </ul>
          <button onClick={() => nav('/login', { state: { from: `/c/${slug}` }})} className="btn-primary text-sm w-full mt-4 inline-flex items-center justify-center gap-2" data-testid="book-now-btn">
            Book a session <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-fg-muted text-center mt-2">New patient? <Link to="/register" className="text-brand underline">Create account</Link></p>
        </aside>

        {/* Clinicians + features */}
        <section className="md:col-span-2 space-y-6">
          <div className="surface border border-soft rounded-xl p-5">
            <h2 className="text-xs uppercase tracking-wider text-fg-muted mb-3">Why patients choose Physiogenic clinics</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {[
                'GDPR + HIPAA-ready medical records',
                'Online booking + automated reminders',
                'Personal exercise programs',
                'Direct messaging with your therapist',
                'Secure digital invoices',
                'Multi-language portal (EN / PL / ES)',
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-brand shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>

          {clinic.clinicians?.length > 0 && (
            <div className="surface border border-soft rounded-xl p-5">
              <h2 className="text-xs uppercase tracking-wider text-fg-muted mb-3">Therapists at this clinic</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {clinic.clinicians.map(c => (
                  <div key={c.user_id} className="border border-soft rounded-lg p-3 flex items-center gap-3" data-testid={`pc-clinician-${c.user_id}`}>
                    <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center text-brand shrink-0">
                      {c.photo_url
                        ? <img src={c.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                        : <User className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-[11px] text-fg-muted">{c.credentials || 'Physiotherapist'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Iter 120 — upcoming clinic closures / public holidays. */}
          {clinic.upcoming_holidays?.length > 0 && (
            <div className="surface border border-soft rounded-xl p-5" data-testid="upcoming-closures">
              <h2 className="text-xs uppercase tracking-wider text-fg-muted mb-3 flex items-center gap-1.5">
                <CalendarX className="w-3.5 h-3.5" /> Upcoming closures
              </h2>
              <ul className="space-y-2">
                {clinic.upcoming_holidays.map(h => {
                  const start = new Date(h.starts_at);
                  const end = new Date(h.ends_at);
                  const sameDay = start.toDateString() === new Date(end.getTime() - 1).toDateString();
                  const rangeLabel = sameDay
                    ? start.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
                    : `${start.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} → ${end.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
                  return (
                    <li key={h.holiday_id} className="flex items-start gap-3 text-sm" data-testid={`holiday-${h.holiday_id}`}>
                      <div className="mt-0.5 w-8 h-8 rounded-md bg-brand/10 text-brand flex items-center justify-center shrink-0">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-fg">{h.name}</div>
                        <div className="text-[11px] text-fg-muted">{rangeLabel}</div>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-fg-muted px-2 py-0.5 rounded-full bg-soft">Closed</div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-fg-muted mt-3">Please avoid booking during these dates — no clinicians will be available.</p>
            </div>
          )}
        </section>
      </main>

      {/* Footer — hidden when the tenant sets hide_powered_by=true (Iter 121). */}
      {!clinic.hide_powered_by && (
        <footer className="text-center py-8 text-xs text-fg-muted" data-testid="powered-by-footer">
          Powered by <Link to="/" className="text-brand hover:underline">Physiogenic</Link>
          <span className="mx-2">·</span>
          <Link to="/start-clinic" className="hover:text-brand">Start your own clinic</Link>
        </footer>
      )}
    </div>
  );
}
